// src/pages/admin/analytics/components/RawMaterialConsumptionReportTab.jsx
import React, { useState, useMemo } from 'react';
import {
  ShoppingCart,
  ShieldAlert,
  Search,
  Download,
  Printer,
  PackageCheck,
  TrendingDown,
  AlertTriangle,
  RefreshCw,
  Info,
  DollarSign,
  Layers,
  ChevronDown,
  ChevronUp,
  Coffee,
  PieChart,
} from 'lucide-react';
import toast from 'react-hot-toast';

const RawMaterialConsumptionReportTab = ({ data, loading, onRefresh }) => {
  const [searchTerm, setSearchTerm] = useState('');
  const [riskFilter, setRiskFilter] = useState('ALL'); // 'ALL' | 'HIGH_RISK_THEFT' | 'MODERATE_VARIANCE' | 'NORMAL'
  const [sortBy, setSortBy] = useState('usageDesc'); // 'usageDesc' | 'consumptionCostDesc' | 'shortageDesc' | 'stockAsc'
  const [expandedItemId, setExpandedItemId] = useState(null);

  const summary = data?.summary || {};
  const variances = data?.variances || [];

  // Compute calculated metrics for consumption report
  const processedItems = useMemo(() => {
    return variances.map((v) => {
      const theoretical = v.theoreticalUsage || 0;
      const wastage = v.manualWastageQty || 0;
      const totalDepletion = v.totalExpectedUsage || (theoretical + wastage);
      const unitPrice = v.unitPrice || 0;
      const totalConsumptionCost = totalDepletion * unitPrice;
      const theoreticalCost = theoretical * unitPrice;
      const wastageCost = wastage * unitPrice;
      const shortageCost = v.estimatedShortageCost || 0;
      const currentStock = v.currentStock || 0;

      // Purchasing recommendation status based on stock vs depletion rate
      let purchaseStatus = 'SUFFICIENT';
      if (currentStock <= 0) {
        purchaseStatus = 'OUT_OF_STOCK';
      } else if (currentStock < totalDepletion * 0.5) {
        purchaseStatus = 'CRITICAL_REORDER';
      } else if (currentStock < totalDepletion) {
        purchaseStatus = 'REORDER_RECOMMENDED';
      }

      return {
        ...v,
        totalDepletion,
        unitPrice,
        totalConsumptionCost,
        theoreticalCost,
        wastageCost,
        shortageCost,
        currentStock,
        purchaseStatus,
        contributingProducts: v.contributingProducts || [],
      };
    });
  }, [variances]);

  // Aggregate KPI metrics
  const totalConsumptionCostAll = useMemo(() => {
    return processedItems.reduce((acc, item) => acc + item.totalConsumptionCost, 0);
  }, [processedItems]);

  const totalTheoreticalCostAll = useMemo(() => {
    return processedItems.reduce((acc, item) => acc + item.theoreticalCost, 0);
  }, [processedItems]);

  const highRiskAlerts = useMemo(() => {
    return processedItems.filter((item) => item.riskLevel === 'HIGH_RISK_THEFT');
  }, [processedItems]);

  // Filter & Sort
  const filteredAndSortedItems = useMemo(() => {
    return processedItems
      .filter((item) => {
        const matchesSearch =
          (item.name || '').toLowerCase().includes(searchTerm.toLowerCase()) ||
          (item.code || '').toLowerCase().includes(searchTerm.toLowerCase());

        const matchesRisk = riskFilter === 'ALL' || item.riskLevel === riskFilter;

        return matchesSearch && matchesRisk;
      })
      .sort((a, b) => {
        if (sortBy === 'usageDesc') return b.totalDepletion - a.totalDepletion;
        if (sortBy === 'consumptionCostDesc') return b.totalConsumptionCost - a.totalConsumptionCost;
        if (sortBy === 'shortageDesc') return b.shortageCost - a.shortageCost;
        if (sortBy === 'stockAsc') return a.currentStock - b.currentStock;
        return 0;
      });
  }, [processedItems, searchTerm, riskFilter, sortBy]);

  // Export to CSV
  const exportToCSV = () => {
    if (filteredAndSortedItems.length === 0) {
      toast.error('No consumption data available to export');
      return;
    }

    const headers = [
      'Raw Material Name',
      'Material Code',
      'Unit Price (LKR)',
      'Theoretical Recipe Usage',
      'Unit',
      'Manual Logged Wastage',
      'Total Expected Depletion',
      'Total Consumption Cost (LKR)',
      'Current Stock',
      'Shortage Loss Cost (LKR)',
      'Purchasing Status',
      'Fraud Risk Level',
    ];

    const rows = filteredAndSortedItems.map((item) => [
      `"${(item.name || '').replace(/"/g, '""')}"`,
      `"${item.code || ''}"`,
      item.unitPrice.toFixed(2),
      item.theoreticalUsage,
      item.unitName,
      item.manualWastageQty,
      item.totalDepletion,
      item.totalConsumptionCost.toFixed(2),
      item.currentStock,
      item.shortageCost.toFixed(2),
      item.purchaseStatus,
      item.riskLevel,
    ]);

    const csvContent = 'data:text/csv;charset=utf-8,' + [headers.join(','), ...rows.map((e) => e.join(','))].join('\n');
    const encodedUri = encodeURI(csvContent);
    const link = document.createElement('a');
    link.setAttribute('href', encodedUri);
    link.setAttribute('download', `Raw_Material_Consumption_Report_${new Date().toISOString().split('T')[0]}.csv`);
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    toast.success('Consumption report CSV downloaded successfully!');
  };

  const handlePrint = () => {
    window.print();
  };

  const toggleExpandRow = (id) => {
    setExpandedItemId((prev) => (prev === id ? null : id));
  };

  if (loading) {
    return (
      <div className="flex flex-col items-center justify-center py-20 text-gray-500 bg-white rounded-2xl border border-gray-200 shadow-sm">
        <RefreshCw className="h-9 w-9 animate-spin text-orange-500 mb-3" />
        <span className="font-bold text-gray-800 text-lg">Calculating Sales & Recipe Raw Material Consumption...</span>
        <p className="text-xs text-gray-500 mt-1">Cross-referencing sold product items with recipe ingredient formulas</p>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Top Banner & Info */}
      <div className="p-6 bg-gradient-to-r from-slate-900 via-indigo-950 to-slate-900 text-white rounded-2xl shadow-lg relative overflow-hidden">
        <div className="absolute top-0 right-0 -mt-8 -mr-8 w-48 h-48 bg-orange-500/10 rounded-full blur-2xl pointer-events-none" />
        <div className="flex flex-col md:flex-row items-start md:items-center justify-between gap-4 relative z-10">
          <div className="flex items-center space-x-4">
            <div className="p-3.5 bg-orange-500/20 border border-orange-500/30 rounded-xl text-orange-400">
              <ShoppingCart className="h-7 w-7" />
            </div>
            <div>
              <div className="flex items-center space-x-2">
                <h2 className="text-xl font-black tracking-tight text-white">Raw Material Consumption & Purchasing Report</h2>
                <span className="px-2.5 py-0.5 bg-emerald-500/20 text-emerald-400 border border-emerald-500/30 text-[10px] font-bold rounded-full uppercase tracking-wider">
                  Read-Only Production Safe
                </span>
              </div>
              <p className="text-xs text-slate-300 mt-1 max-w-2xl">
                Calculates raw material consumption derived directly from sold menu items × recipe proportions. Click any item to view which sales orders consumed it.
              </p>
            </div>
          </div>

          <div className="flex items-center space-x-2 self-end md:self-auto">
            <button
              onClick={exportToCSV}
              className="flex items-center space-x-2 px-4 py-2.5 bg-orange-600 hover:bg-orange-500 text-white font-bold text-xs rounded-xl shadow-md transition-all active:scale-95 cursor-pointer"
            >
              <Download className="h-4 w-4" />
              <span>Export CSV</span>
            </button>
            <button
              onClick={handlePrint}
              className="flex items-center space-x-2 px-4 py-2.5 bg-white/10 hover:bg-white/20 text-white font-bold text-xs rounded-xl border border-white/10 transition-all cursor-pointer"
            >
              <Printer className="h-4 w-4" />
              <span>Print</span>
            </button>
          </div>
        </div>
      </div>

      {/* Fraud & Theft Alert Banner */}
      {highRiskAlerts.length > 0 && (
        <div className="p-5 bg-gradient-to-r from-red-600 via-rose-600 to-amber-600 text-white rounded-2xl shadow-md border border-red-400 flex items-start space-x-4">
          <div className="p-3 bg-white/20 rounded-xl flex-shrink-0">
            <ShieldAlert className="h-7 w-7 text-white" />
          </div>
          <div className="flex-1">
            <div className="flex items-center justify-between">
              <h3 className="text-base font-black tracking-wide">
                ⚠️ FRAUD & THEFT ALERT: {highRiskAlerts.length} Raw Material(s) Exceed Risk Thresholds
              </h3>
              <span className="px-3 py-1 bg-white text-red-900 font-black rounded-full text-[10px] uppercase tracking-wider">
                Physical Audit Required
              </span>
            </div>
            <p className="text-xs text-red-100 mt-1">
              High discrepancy detected between calculated recipe depletion and actual inventory levels. Review wastage entries and stock movement for the following items:
            </p>
            <div className="mt-3 flex flex-wrap gap-2">
              {highRiskAlerts.map((ha) => (
                <span
                  key={ha.rawMaterialId}
                  className="px-3 py-1 bg-black/30 backdrop-blur-sm text-white rounded-lg text-xs font-semibold border border-white/20 flex items-center space-x-1"
                >
                  <span>{ha.name}</span>
                  <span className="text-amber-300">
                    ({ha.totalDepletion} {ha.unitName} consumed • Shortage: Rs. {ha.shortageCost.toFixed(2)})
                  </span>
                </span>
              ))}
            </div>
          </div>
        </div>
      )}

      {/* KPI Cards */}
      <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-4 gap-4">
        {/* Total Consumption Cost */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Total Consumption Value</p>
            <div className="p-2 bg-blue-50 text-blue-600 rounded-lg">
              <DollarSign className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-gray-900 mt-2">
            Rs. {totalConsumptionCostAll.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <div className="flex items-center justify-between mt-2 text-[11px] text-gray-500">
            <span>Theoretical: Rs. {totalTheoreticalCostAll.toLocaleString('en-US', { maximumFractionDigits: 0 })}</span>
            <span className="font-semibold text-blue-700">Cost of Goods Used</span>
          </div>
        </div>

        {/* Tracked Materials */}
        <div className="bg-white p-5 rounded-2xl border border-gray-200 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-gray-500 uppercase tracking-wider">Tracked Raw Materials</p>
            <div className="p-2 bg-purple-50 text-purple-600 rounded-lg">
              <Layers className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-purple-950 mt-2">{processedItems.length}</p>
          <p className="text-xs text-purple-700 mt-2 font-medium">Active recipe ingredients in period</p>
        </div>

        {/* Shortage & Theft Cost */}
        <div className="bg-white p-5 rounded-2xl border border-amber-200 bg-amber-50/20 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-amber-700 uppercase tracking-wider">Unaccounted Shortage Cost</p>
            <div className="p-2 bg-amber-100 text-amber-700 rounded-lg">
              <TrendingDown className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-amber-900 mt-2">
            Rs. {(summary.totalShortageCost || 0).toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
          </p>
          <p className="text-xs text-amber-700 mt-2 font-medium">Potential inventory leakage / fraud loss</p>
        </div>

        {/* Fraud Risk Alerts Count */}
        <div className="bg-white p-5 rounded-2xl border border-red-200 bg-red-50/20 shadow-sm relative overflow-hidden">
          <div className="flex items-center justify-between">
            <p className="text-xs font-bold text-red-700 uppercase tracking-wider">High Theft Risk Items</p>
            <div className="p-2 bg-red-100 text-red-700 rounded-lg">
              <AlertTriangle className="h-5 w-5" />
            </div>
          </div>
          <p className="text-2xl font-black text-red-900 mt-2">{highRiskAlerts.length}</p>
          <p className="text-xs text-red-700 mt-2 font-medium">Require physical count verification</p>
        </div>
      </div>

      {/* Toolbar & Filters */}
      <div className="bg-white p-4 rounded-2xl border border-gray-200 shadow-sm flex flex-col md:flex-row items-center justify-between gap-4">
        {/* Search */}
        <div className="relative w-full md:w-80">
          <Search className="absolute left-3.5 top-1/2 -translate-y-1/2 h-4 w-4 text-gray-400" />
          <input
            type="text"
            placeholder="Search raw material name or code..."
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 rounded-xl text-xs bg-gray-50 focus:bg-white focus:ring-2 focus:ring-orange-500 focus:border-orange-500 transition-all"
          />
        </div>

        {/* Filter Controls */}
        <div className="flex flex-wrap items-center gap-3 w-full md:w-auto justify-end">
          {/* Risk Filter */}
          <div className="flex items-center space-x-1 bg-gray-100 p-1 rounded-xl border border-gray-200 text-xs font-semibold text-gray-700">
            <button
              onClick={() => setRiskFilter('ALL')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                riskFilter === 'ALL' ? 'bg-white text-gray-900 shadow-sm font-bold' : 'hover:text-gray-900'
              }`}
            >
              All Items
            </button>
            <button
              onClick={() => setRiskFilter('HIGH_RISK_THEFT')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                riskFilter === 'HIGH_RISK_THEFT' ? 'bg-red-600 text-white shadow-sm font-bold' : 'hover:text-red-700'
              }`}
            >
              ⚠️ High Fraud Risk
            </button>
            <button
              onClick={() => setRiskFilter('MODERATE_VARIANCE')}
              className={`px-3 py-1.5 rounded-lg transition-all cursor-pointer ${
                riskFilter === 'MODERATE_VARIANCE' ? 'bg-amber-500 text-white shadow-sm font-bold' : 'hover:text-amber-700'
              }`}
            >
              ⚡ Moderate
            </button>
          </div>

          {/* Sort Selector */}
          <div className="relative">
            <select
              value={sortBy}
              onChange={(e) => setSortBy(e.target.value)}
              className="px-3 py-2 border border-gray-300 rounded-xl bg-white text-gray-800 text-xs font-bold shadow-sm focus:ring-2 focus:ring-orange-500 hover:border-gray-400 cursor-pointer"
            >
              <option value="usageDesc">Sort by Highest Theoretical Usage Qty</option>
              <option value="consumptionCostDesc">Sort by Highest Consumption Value (LKR)</option>
              <option value="shortageDesc">Sort by Highest Fraud Shortage Cost</option>
              <option value="stockAsc">Sort by Lowest Current Stock</option>
            </select>
          </div>
        </div>
      </div>

      {/* Main Table */}
      <div className="bg-white rounded-2xl shadow-sm border border-gray-200 overflow-hidden">
        <div className="p-4 bg-gray-50/80 border-b border-gray-200 flex items-center justify-between">
          <div>
            <h3 className="font-bold text-gray-900 text-sm flex items-center">
              <ShoppingCart className="h-4 w-4 text-orange-500 mr-2" />
              Raw Material Consumption & Inventory Discrepancy Matrix
            </h3>
            <p className="text-xs text-gray-500">
              Showing {filteredAndSortedItems.length} of {processedItems.length} raw materials based on active filters
            </p>
          </div>
        </div>

        <div className="overflow-x-auto">
          <table className="w-full text-left text-xs">
            <thead className="bg-gray-100 border-b border-gray-200 text-gray-600 font-bold uppercase tracking-wider">
              <tr>
                <th className="p-4 w-10"></th>
                <th className="p-4">Raw Material Details</th>
                <th className="p-4 text-right">Unit Cost (LKR)</th>
                <th className="p-4 text-center">Theoretical Recipe Usage</th>
                <th className="p-4 text-center">Manual Logged Wastage</th>
                <th className="p-4 text-center">Total Expected Depletion</th>
                <th className="p-4 text-right">Total Consumption Cost (LKR)</th>
                <th className="p-4 text-center">Current Stock</th>
                <th className="p-4 text-center">Stock & Replenish Status</th>
                <th className="p-4 text-right">Fraud Shortage Cost (LKR)</th>
                <th className="p-4 text-center">Fraud Risk Level</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-gray-100 font-medium">
              {filteredAndSortedItems.length === 0 ? (
                <tr>
                  <td colSpan="11" className="p-12 text-center text-gray-500">
                    <PackageCheck className="h-10 w-10 text-gray-300 mx-auto mb-2" />
                    <p className="font-bold text-gray-700">No matching raw material consumption data</p>
                    <p className="text-xs text-gray-400 mt-1">Try adjusting your search filters or select a different date range.</p>
                  </td>
                </tr>
              ) : (
                filteredAndSortedItems.map((item) => {
                  const isHighRisk = item.riskLevel === 'HIGH_RISK_THEFT';
                  const isModerate = item.riskLevel === 'MODERATE_VARIANCE';
                  const isExpanded = expandedItemId === item.rawMaterialId;
                  const hasProducts = item.contributingProducts && item.contributingProducts.length > 0;

                  return (
                    <React.Fragment key={item.rawMaterialId}>
                      <tr
                        onClick={() => hasProducts && toggleExpandRow(item.rawMaterialId)}
                        className={`hover:bg-orange-50/40 transition-colors ${
                          hasProducts ? 'cursor-pointer' : ''
                        } ${
                          isHighRisk ? 'bg-red-50/40' : isModerate ? 'bg-amber-50/30' : ''
                        } ${isExpanded ? 'bg-orange-50/80 border-l-4 border-orange-500' : ''}`}
                      >
                        {/* Expand Toggle Icon */}
                        <td className="p-4 text-center">
                          {hasProducts ? (
                            isExpanded ? (
                              <ChevronUp className="h-4 w-4 text-orange-600" />
                            ) : (
                              <ChevronDown className="h-4 w-4 text-gray-400 hover:text-orange-600" />
                            )
                          ) : (
                            <span className="text-gray-300 text-[10px]">•</span>
                          )}
                        </td>

                        {/* Name & Code */}
                        <td className="p-4">
                          <div className="flex items-center space-x-2">
                            <div>
                              <p className="font-bold text-gray-900">{item.name}</p>
                              <div className="flex items-center space-x-1.5 mt-0.5">
                                <span className="text-[10px] font-mono px-1.5 py-0.5 bg-gray-100 text-gray-600 rounded">
                                  {item.code}
                                </span>
                                {hasProducts && (
                                  <span className="text-[10px] text-blue-600 font-bold bg-blue-50 px-1.5 py-0.5 rounded">
                                    {item.contributingProducts.length} Recipe Product(s)
                                  </span>
                                )}
                              </div>
                            </div>
                          </div>
                        </td>

                        {/* Unit Cost */}
                        <td className="p-4 text-right font-semibold text-gray-700">
                          Rs. {item.unitPrice.toFixed(2)}
                        </td>

                        {/* Theoretical Usage */}
                        <td className="p-4 text-center">
                          <span className={`font-black ${item.theoreticalUsage > 0 ? 'text-blue-700 text-sm' : 'text-gray-500'}`}>
                            {item.theoreticalUsage} {item.unitName}
                          </span>
                          <p className="text-[10px] text-gray-400">
                            Rs. {item.theoreticalCost.toFixed(2)}
                          </p>
                        </td>

                        {/* Manual Wastage */}
                        <td className="p-4 text-center">
                          {item.manualWastageQty > 0 ? (
                            <div>
                              <span className="font-bold text-rose-600">
                                {item.manualWastageQty} {item.unitName}
                              </span>
                              <p className="text-[10px] text-rose-400">Rs. {item.wastageCost.toFixed(2)}</p>
                            </div>
                          ) : (
                            <span className="text-gray-400">-</span>
                          )}
                        </td>

                        {/* Total Expected Depletion */}
                        <td className="p-4 text-center font-black text-gray-900">
                          {item.totalDepletion} {item.unitName}
                        </td>

                        {/* Total Consumption Cost */}
                        <td className="p-4 text-right font-black text-gray-900">
                          Rs. {item.totalConsumptionCost.toLocaleString('en-US', { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                        </td>

                        {/* Current Stock */}
                        <td className="p-4 text-center font-bold text-slate-700">
                          {item.currentStock} {item.unitName}
                        </td>

                        {/* Stock Replenish Status */}
                        <td className="p-4 text-center">
                          {item.purchaseStatus === 'OUT_OF_STOCK' && (
                            <span className="px-2 py-0.5 bg-red-100 text-red-800 font-bold rounded-md text-[10px]">
                              ❌ Out of Stock
                            </span>
                          )}
                          {item.purchaseStatus === 'CRITICAL_REORDER' && (
                            <span className="px-2 py-0.5 bg-amber-100 text-amber-800 font-bold rounded-md text-[10px]">
                              ⚠️ Reorder ASAP
                            </span>
                          )}
                          {item.purchaseStatus === 'REORDER_RECOMMENDED' && (
                            <span className="px-2 py-0.5 bg-blue-100 text-blue-800 font-bold rounded-md text-[10px]">
                              📦 Low Stock
                            </span>
                          )}
                          {item.purchaseStatus === 'SUFFICIENT' && (
                            <span className="px-2 py-0.5 bg-emerald-100 text-emerald-800 font-bold rounded-md text-[10px]">
                              ✓ Sufficient Stock
                            </span>
                          )}
                        </td>

                        {/* Shortage Cost */}
                        <td className="p-4 text-right font-black text-amber-700">
                          Rs. {item.shortageCost.toFixed(2)}
                        </td>

                        {/* Risk Level */}
                        <td className="p-4 text-center">
                          <span
                            className={`inline-flex items-center px-2.5 py-1 rounded-full text-[10px] font-bold ${
                              isHighRisk
                                ? 'bg-red-600 text-white shadow-sm'
                                : isModerate
                                ? 'bg-amber-500 text-white shadow-sm'
                                : 'bg-emerald-100 text-emerald-800'
                            }`}
                          >
                            {isHighRisk ? '⚠️ High Theft Risk' : isModerate ? '⚡ Moderate' : '✓ Normal'}
                          </span>
                        </td>
                      </tr>

                      {/* Expandable Recipe Sales Breakdown Panel */}
                      {isExpanded && hasProducts && (
                        <tr className="bg-slate-50 border-b border-orange-200">
                          <td colSpan="11" className="p-4 pl-12">
                            <div className="bg-white p-4 rounded-xl border border-orange-200 shadow-sm space-y-3">
                              <div className="flex items-center justify-between border-b border-gray-100 pb-2">
                                <div className="flex items-center space-x-2">
                                  <Coffee className="h-4 w-4 text-orange-500" />
                                  <h4 className="font-bold text-gray-900 text-xs">
                                    Product Sales & Recipe Consumption Formula Breakdown for <span className="text-orange-600">{item.name}</span>
                                  </h4>
                                </div>
                                <span className="text-[11px] text-gray-500">
                                  Formula: (Sold Product Units) × (Ingredient Recipe Ratio) = Total Consumption
                                </span>
                              </div>

                              <div className="overflow-x-auto">
                                <table className="w-full text-left text-xs bg-gray-50/50 rounded-lg overflow-hidden border border-gray-200">
                                  <thead className="bg-gray-100 text-gray-600 font-bold uppercase text-[10px]">
                                    <tr>
                                      <th className="p-2.5">Sold Product Name</th>
                                      <th className="p-2.5">SKU / Code</th>
                                      <th className="p-2.5 text-center">Units Sold</th>
                                      <th className="p-2.5 text-center">Recipe Ingredient Ratio</th>
                                      <th className="p-2.5 text-right font-black">Calculated Consumption</th>
                                    </tr>
                                  </thead>
                                  <tbody className="divide-y divide-gray-200">
                                    {item.contributingProducts.map((cp, idx) => (
                                      <tr key={idx} className="hover:bg-white transition-colors">
                                        <td className="p-2.5 font-bold text-gray-800 flex items-center space-x-2">
                                          <span>☕ {cp.productName}</span>
                                        </td>
                                        <td className="p-2.5 text-gray-500 font-mono text-[10px]">{cp.sku || '-'}</td>
                                        <td className="p-2.5 text-center font-bold text-emerald-700 bg-emerald-50/50">
                                          {cp.qtySold} sold
                                        </td>
                                        <td className="p-2.5 text-center text-gray-600">
                                          {cp.recipeIngredientQty} {item.unitName} / unit
                                        </td>
                                        <td className="p-2.5 text-right font-black text-blue-700">
                                          {cp.totalIngQty} {item.unitName}
                                        </td>
                                      </tr>
                                    ))}
                                  </tbody>
                                </table>
                              </div>
                            </div>
                          </td>
                        </tr>
                      )}
                    </React.Fragment>
                  );
                })
              )}
            </tbody>
          </table>
        </div>
      </div>

      {/* Guidance Footer */}
      <div className="p-4 bg-blue-50 border border-blue-200 rounded-xl flex items-start space-x-3 text-xs text-blue-800">
        <Info className="h-5 w-5 text-blue-600 flex-shrink-0 mt-0.5" />
        <div>
          <h4 className="font-bold">Purchasing & Goods Ordering Guidance</h4>
          <p className="mt-0.5 text-blue-700">
            <strong>Theoretical Recipe Usage</strong> reflects sales volumes multiplied by ingredient recipe ratios. <strong>Total Expected Depletion</strong> includes logged manual wastage. If actual stock level drops below expected depletion, create a Purchase Order (PO) or Good Received Note (GRN) to replenish stock, and check items marked as <em>High Theft Risk</em> for store leakage.
          </p>
        </div>
      </div>
    </div>
  );
};

export default RawMaterialConsumptionReportTab;
